import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";

const MileageRateNewEdit = () => {
  usePageTitle("Edit Mileage Rate");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const {
    overlayData,
    setOverlayName,
    showSnackbar,
    handleErrorDialog,
  } = useStateContext();

  const isEditMode = !!overlayData?.mileageRateId;
  const allMileageRates = overlayData?.allMileageRates || [];

  // Edit lock for existing mileage rates only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "mileageRate",
    isEditMode ? overlayData?.mileageRateId : null,
  );

  const [year, setYear] = useState(overlayData?.year?.toString() || "");
  const [rate, setRate] = useState(overlayData?.rate?.toString() || "");
  const [initialYear] = useState(overlayData?.year?.toString() || "");
  const [initialRate] = useState(overlayData?.rate?.toString() || "");
  const [saving, setSaving] = useState(false);

  const closeOverlay = () => setOverlayName("");

  // Check if year already exists (excluding current rate if editing)
  const isDuplicateYear = allMileageRates.some(
    (rate) =>
      parseInt(rate.year) === parseInt(year || 0) &&
      rate.id !== overlayData?.mileageRateId,
  );

  // Check if year is a valid number
  const isValidYear = year && !isNaN(parseInt(year)) && parseInt(year) > 0;
  const isValidRate = rate && !isNaN(parseFloat(rate)) && parseFloat(rate) > 0;

  const handleSave = async () => {
    if (!year.trim()) {
      showSnackbar("Year is required", theme, "error");
      return;
    }

    if (!isValidYear) {
      showSnackbar("Year must be a valid positive number", theme, "error");
      return;
    }

    if (!rate.trim()) {
      showSnackbar("Rate is required", theme, "error");
      return;
    }

    if (!isValidRate) {
      showSnackbar("Rate must be a valid positive number", theme, "error");
      return;
    }

    if (isDuplicateYear) {
      showSnackbar(
        `Mileage rate for year ${year} already exists`,
        theme,
        "error",
      );
      return;
    }

    const yearChanged = year.trim() !== initialYear.trim();
    const rateChanged = rate.trim() !== initialRate.trim();

    if (!yearChanged && !rateChanged) {
      showSnackbar("No changes to save", theme, "info");
      return;
    }

    setSaving(true);

    try {
      if (isEditMode) {
        // Update existing rate (only rate can be changed, not year)
        await api.put(`/mileageRates/${overlayData.mileageRateId}`, {
          year: parseInt(year),
          rate: parseFloat(rate),
        });
        showSnackbar("Mileage rate updated successfully", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "MileageRate",
            recordId: overlayData.mileageRateId,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      } else {
        // Create new rate
        const response = await api.post("/mileageRates", {
          year: parseInt(year),
          rate: parseFloat(rate),
        });
        showSnackbar("Mileage rate created successfully", theme, "success");

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit("settings:saved", {
            settingsType: "MileageRate",
            recordId: response.data.id,
            changeAction: "Created",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }

      // Trigger callback to refresh rates list
      overlayData?.onMileageRatesSaved?.();
      setSaving(false);
      closeOverlay();
    } catch (err) {
      console.error(err);
      handleErrorDialog(
        err.response?.data?.error || "Error saving mileage rate",
        theme,
      );
      setSaving(false);
    }
  };

  const pageTitle = isEditMode ? "Edit Mileage Rate" : "New Mileage Rate";

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={closeOverlay}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "800px",
          maxHeight: "80%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: "0.5rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "60%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {pageTitle}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              width: "40%",
              flexDirection: "row",
              gap: 1,
            }}
          >
            {isMobile ? (
              <Tooltip title="Discard" placement="top">
                <IconButton
                  color="primary"
                  onClick={closeOverlay}
                  sx={{
                    width: "40px",
                    height: "40px",
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: "12px",
                  }}
                >
                  <DisabledByDefaultRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                color="primary"
                variant="outlined"
                size="large"
                onClick={closeOverlay}
                sx={{
                  borderRadius: "12px",
                }}
              >
                Discard
              </Button>
            )}

            <Tooltip
              title={
                isMobile &&
                (saving || isDuplicateYear || !isValidYear || !isValidRate)
                  ? "Please fix errors before saving"
                  : ""
              }
              placement="top"
            >
              <span>
                {isMobile ? (
                  <IconButton
                    color="primary"
                    onClick={handleSave}
                    disabled={
                      saving || isDuplicateYear || !isValidYear || !isValidRate
                    }
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.background.default,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&.Mui-disabled": {
                        backgroundColor:
                          theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                ) : (
                  <Button
                    loading={saving}
                    color="primary"
                    variant="contained"
                    size="large"
                    endIcon={<SaveRoundedIcon />}
                    onClick={handleSave}
                    disabled={
                      saving || isDuplicateYear || !isValidYear || !isValidRate
                    }
                    sx={{
                      borderRadius: "12px",
                    }}
                  >
                    Save
                  </Button>
                )}
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Mileage Rate Details
              </Typography>
            </Grid>

            {isDuplicateYear && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">
                  A mileage rate for year {year} already exists in the system.
                </Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Year"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                inputProps={{ maxLength: 4 }}
                placeholder="e.g., 2025"
                error={!isValidYear && year !== ""}
                disabled={isEditMode}
                helperText={isEditMode ? "Year cannot be changed" : ""}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Rate ($ per mile)"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                inputProps={{ maxLength: 8, step: 0.01 }}
                placeholder="e.g., 0.67"
                error={!isValidRate && rate !== ""}
                type="number"
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </Box>
  );
};

export default MileageRateNewEdit;
